/**
 * Hooks de equipos (mejora D del changelog cliente).
 *
 * Cubre el CRUD de la página `/configuracion/equipos` (solo admin) y un
 * selector ligero para el wizard de nueva licitación (selector de equipo
 * de los que el user es miembro).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";
import type { components } from "@/lib/api/generated";

export type TeamSummary = components["schemas"]["TeamSummary"];
export type TeamRead = components["schemas"]["TeamRead"];
export type TeamCreate = components["schemas"]["TeamCreate"];
export type TeamUpdate = components["schemas"]["TeamUpdate"];

const TEAMS_KEY = ["teams"] as const;

export function useTeams(options?: { onlyMine?: boolean }) {
  const onlyMine = options?.onlyMine ?? false;
  return useQuery<TeamSummary[], Error>({
    queryKey: [...TEAMS_KEY, { onlyMine }],
    queryFn: () =>
      fetchWithAuth(`/api/v1/teams${onlyMine ? "?only_mine=true" : ""}`),
    staleTime: 60_000,
  });
}

export function useTeam(teamId: string | null | undefined) {
  return useQuery<TeamRead, Error>({
    queryKey: ["team", teamId],
    queryFn: () => fetchWithAuth(`/api/v1/teams/${teamId}`),
    enabled: !!teamId,
  });
}

export function useCreateTeam() {
  const queryClient = useQueryClient();
  return useMutation<TeamRead, Error, TeamCreate>({
    mutationFn: (payload) =>
      fetchWithAuth("/api/v1/teams", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAMS_KEY });
      showToast.success("Equipo creado");
    },
    onError: (e) => showToast.error(e.message || "Error al crear el equipo"),
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();
  return useMutation<TeamRead, Error, { id: string; data: TeamUpdate }>({
    mutationFn: ({ id, data }) =>
      fetchWithAuth(`/api/v1/teams/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: TEAMS_KEY });
      queryClient.invalidateQueries({ queryKey: ["team", id] });
      showToast.success("Equipo actualizado");
    },
    onError: (e) => showToast.error(e.message || "Error al actualizar"),
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      fetchWithAuth(`/api/v1/teams/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAMS_KEY });
      showToast.success("Equipo eliminado");
    },
    onError: (e) => showToast.error(e.message || "Error al eliminar"),
  });
}

export function useAddTeamMember() {
  const queryClient = useQueryClient();
  return useMutation<
    TeamRead,
    Error,
    { teamId: string; userId: string }
  >({
    mutationFn: ({ teamId, userId }) =>
      fetchWithAuth(`/api/v1/teams/${teamId}/members`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
      }),
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: TEAMS_KEY });
      queryClient.invalidateQueries({ queryKey: ["team", teamId] });
      showToast.success("Miembro añadido");
    },
    onError: (e) => showToast.error(e.message || "Error al añadir miembro"),
  });
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { teamId: string; userId: string }
  >({
    mutationFn: ({ teamId, userId }) =>
      fetchWithAuth(`/api/v1/teams/${teamId}/members/${userId}`, {
        method: "DELETE",
      }),
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: TEAMS_KEY });
      queryClient.invalidateQueries({ queryKey: ["team", teamId] });
      showToast.success("Miembro eliminado");
    },
    onError: (e) => showToast.error(e.message || "Error al quitar miembro"),
  });
}
